import { lazy, useEffect, useRef, useState } from 'react'
import {
    BlockWrapper,
    ChildrenBlock,
    defineBlock,
    smartProps,
    useEditor,
    useInspectorControl,
} from '@smartfire/js-block-engine'
import useMediaQuery from '@mui/material/useMediaQuery'
import clsx from 'clsx'
import style from './style.module.scss'

const Inspector = lazy(() => import('./inspector'))

const allowedBlocks = ['core/heading', 'core/paragraph', 'smartfire/button']
const templateBlocks = [['core/heading'], ['core/paragraph'], ['smartfire/button']]

const isPopupClosed = () => {
    const cookies = document.cookie.split('; ')
    for (const cookie of cookies) {
        const [name, value] = cookie.split('=')
        if (name === 'popup_closed' && value === '1') {
            return true
        }
    }
    return false
}

const PopupBlock = ({
    backgroundImage,
    backgroundMobileImage,
    decorationImage,
    darkenPercent,
    activated,
    contentBgDesktop,
    contentBgMobile,
    bgColorDesktop,
    bgColorMobile,
}) => {
    useInspectorControl(Inspector)

    const isMobile = useMediaQuery(`(max-width:${style.mobileBreakpoint})`)
    const [visible, setVisible] = useState(!isPopupClosed())
    const { isEditing } = useEditor()
    const popupRef = useRef(null)

    // Ferme la popup + créer un cookie pour ne plus l'afficher
    const handleClose = () => {
        setVisible(false)
        const expirationTime = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        document.cookie = `popup_closed=1; expires=${expirationTime.toUTCString()}; path=/`
    }

    // Clic sur l'overlay pour fermer
    const handleClickOutside = (event) => {
        if (visible && popupRef.current && !popupRef.current.contains(event.target)) {
            handleClose()
        }
    }

    // Echap pour fermer
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleClose()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    // Focus sur l'élément (accessibilité)
    useEffect(() => {
        if (visible && popupRef.current) {
            popupRef.current.focus()
        }
    }, [visible])

    const popupStyle = {
        backgroundImage: backgroundImage
            ? isMobile && backgroundMobileImage
                ? `url(${backgroundMobileImage.url})`
                : `url(${backgroundImage.url})`
            : undefined,
        '--darkenPercent': darkenPercent,
    }

    const contentStyle = {
        backgroundColor:
            (isMobile && contentBgMobile && bgColorMobile) ||
            (!isMobile && contentBgDesktop && bgColorDesktop),
    }

    return isEditing || activated ? (
        <BlockWrapper.Div
            className={clsx(
                style.block,
                isEditing ? style.admin : style.frontend,
                isEditing || visible ? style.visible : style.hidden,
            )}
            onClick={handleClickOutside}
            aria-hidden={!visible}
        >
            <div
                className={clsx(
                    style.popup,
                    isEditing ? style.admin : style.frontend,
                    darkenPercent > 0 && style.darken,
                )}
                style={popupStyle}
                ref={popupRef}
                tabIndex={-1}
            >
                <button className={style.close} aria-label="Fermer" onClick={handleClose} />
                <div
                    className={clsx(
                        style.content,
                        (isMobile && contentBgMobile) ||
                            (!isMobile && contentBgDesktop && style.bgColor),
                    )}
                    style={contentStyle}
                >
                    <ChildrenBlock
                        gutenbergEditorConfig={{
                            orientation: 'vertical',
                            allowedBlocks: allowedBlocks,
                            template: templateBlocks,
                        }}
                        className={style.children}
                    />
                    {decorationImage && (
                        <img
                            className={style.decoration}
                            src={decorationImage.url}
                            alt={decorationImage.title}
                        />
                    )}
                </div>
            </div>
        </BlockWrapper.Div>
    ) : (
        <BlockWrapper.Without />
    )
}

export default defineBlock({
    Block: PopupBlock,
    smartProps: {
        backgroundImage: smartProps.image,
        backgroundMobileImage: smartProps.image,
        decorationImage: smartProps.image,
        darkenPercent: smartProps.number.withDefault(0),
        activated: smartProps.bool.withDefault(true),
        contentBgDesktop: smartProps.bool.withDefault(true),
        contentBgMobile: smartProps.bool.withDefault(true),
        bgColorDesktop: smartProps.string.withDefault(style.white),
        bgColorMobile: smartProps.string.withDefault(style.white),
    },
    setup: {
        title: 'Pop-up',
    },
})
